import React, { useState, useEffect } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { PreviewSection } from "../shared";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { InvoiceType, INVOICE_TERMS } from "@/types/db/invoices";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Button } from "@/components/ui/Button";
import { EnumInput } from "@/components/ui/EnumInput";
import { DateInput } from "@/components/ui/DateInput";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import { ModalCard } from "@/components/Modals/ModalCard";
import { TenantAddressType } from "@/types/db/tenants";
import { formatCurrency } from "@/lib/format/currency";
import { supabase } from "@/lib/supabase-client";
import { tenantService } from "@/services/tenantService";
import useToast from "@/hooks/useToast";
import { useModal } from "@/hooks/useModal";
import useDateTime from "@/hooks/useDateTime";
import { PdfModal } from "@/components/Modals/PdfModal";
import { groupLineItems, hasGroupingData } from "./utils/groupLineItems";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { combineLineItems } from "./utils/combineLineItems";
import { NumericInput } from "@/components/ui/NumericInput";

export interface InvoiceOverviewTabProps {
  invoice: InvoiceType | null;
  loading?: boolean;
}

/** Round to nearest cent. */
const round2 = (n: number) => Math.round(n * 100) / 100;

export function InvoiceOverviewTab({ invoice, loading = false }: InvoiceOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<InvoiceType>();
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const { formatDate: formatLocaleDate } = useDateTime();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentPdfSrc, setCurrentPdfSrc] = useState<string>("");
  const [addresses, setAddresses] = useState<TenantAddressType[]>([]);
  const [tenantName, setTenantName] = useState("");

  // Fetch tenant name and addresses on mount
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const tenantData = await tenantService.getTenantNameAndMeta();
        if (!cancel) {
          setTenantName(tenantData?.name ?? "");
          setAddresses(tenantData?.meta_data?.addresses ?? []);
        }
      } catch (err) {
        console.error("Failed to fetch tenant data:", err);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  // Unified data source
  const displayData = editing ? formData : invoice;
  const disabled = loading || !editing;

  const combineAdminFee = displayData?.meta_data?.combine_admin_fee ?? true;
  const combineDiscount = displayData?.meta_data?.combine_discount ?? true;

  const toggleCombineAdminFee = () => {
    if (!editing) return;
    updateFormData({
      ...formData,
      meta_data: { ...formData.meta_data, combine_admin_fee: !combineAdminFee },
    } as any);
  };

  const toggleCombineDiscount = () => {
    if (!editing) return;
    updateFormData({
      ...formData,
      meta_data: { ...formData.meta_data, combine_discount: !combineDiscount },
    } as any);
  };

  // Local adjustments list (UI-only for now)
  const [adjustments, setAdjustments] = useState<{ id: number; description: string; amount: number }[]>([]);
  const [nextAdjId, setNextAdjId] = useState(1);

  const addAdjustment = () => {
    setAdjustments((prev) => [...prev, { id: nextAdjId, description: "", amount: 0 }]);
    setNextAdjId((n) => n + 1);
  };

  const updateAdjustment = (id: number, patch: { description?: string; amount?: number }) => {
    setAdjustments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  const removeAdjustment = (id: number) => {
    setAdjustments((prev) => prev.filter((a) => a.id !== id));
  };

  // Update modal when PDF src changes
  useEffect(() => {
    if (pdfModalOpen && currentPdfSrc && invoice) {
      openModal(
        <ModalCard>
          <PdfModal
            src={currentPdfSrc}
            onClose={() => {
              closeModal();
              setPdfModalOpen(false);
            }}
            onRegenerate={handleRegeneratePDF}
            entityId={invoice.id}
            entityType="invoice"
            invoiceNumber={invoice.meta_data?.invoice_number}
            projectName={invoice.project?.name}
            invoiceDate={invoice.meta_data?.invoice_date ? formatLocaleDate(invoice.meta_data.invoice_date) : ""}
            amountDue={formatCurrency(invoice.meta_data?.amount_due)}
            loading={isGeneratingPDF}
          />
        </ModalCard>,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPdfSrc, pdfModalOpen, isGeneratingPDF]);

  const formatDate = (date?: Date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleDateString();
  };

  const calculateDueDate = (invoiceDate?: Date, terms?: string) => {
    if (!invoiceDate) return "Not set";
    const days = terms ? parseInt(terms.replace(/\D/g, ""), 10) : 0;
    if (!days) return formatDate(invoiceDate);
    const due = new Date(invoiceDate);
    due.setDate(due.getDate() + days);
    return due.toLocaleDateString();
  };

  // Look up current remittance address name
  const currentAddressName = (() => {
    const addrId = displayData?.meta_data?.remittance_address_id;
    if (!addrId) return "";
    const found = addresses.find((a) => a.id === addrId);
    return found?.name || "";
  })();

  const getRemittanceAddress = (): string | undefined => {
    const addrId = invoice?.meta_data?.remittance_address_id;
    if (!addrId) return undefined;
    const addr = addresses.find((a) => a.id === addrId);
    if (!addr) return undefined;
    return [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(", ");
  };

  const getClientAddress = (): string | undefined => {
    const addr = (invoice?.project?.client as any)?.meta_data?.address;
    if (!addr) return undefined;
    return [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(", ");
  };

  const calculateLineItemTotal = () => {
    const lineItems = displayData?.meta_data?.line_items || [];
    return lineItems.reduce((total, item) => total + (item.amount || 0), 0);
  };

  const projectDeposit = (invoice?.project as any)?.meta_data?.deposit_amount as number | undefined;
  const depositApplied = displayData?.meta_data?.deposit_applied ?? 0;

  const handleDepositChange = (value: number) => {
    const max = typeof projectDeposit === "number" ? projectDeposit : 0;
    const clamped = Math.min(Math.max(value, 0), max);
    updateFormData({
      ...formData,
      meta_data: { ...formData.meta_data, deposit_applied: clamped || undefined },
    } as any);
  };

  const generatePDF = async (
    attachments?: { data: string; name: string; fit: string; align: string; valign: string }[],
  ) => {
    if (!invoice) return null;

    const lineItems = invoice.meta_data?.line_items || [];
    const subtotal = calculateLineItemTotal();

    // Build grouped line items for PDF if grouping data is present
    const useGrouping = hasGroupingData(lineItems);
    const groups = useGrouping ? groupLineItems(lineItems) : undefined;

    // Transform invoice data to pdf-create function format
    const pdfCombineAdminFee = invoice.meta_data?.combine_admin_fee ?? true;
    const pdfCombineDiscount = invoice.meta_data?.combine_discount ?? true;

    const pdfData = {
      action: "invoice",
      combineAdminFee: pdfCombineAdminFee,
      combineDiscount: pdfCombineDiscount,
      headers: {
        invoiceNumber: invoice.meta_data?.invoice_number || "N/A",
        invoiceDate: formatDate(invoice.meta_data?.invoice_date),
        dueDate: calculateDueDate(invoice.meta_data?.invoice_date, invoice.meta_data?.terms),
        clientName: invoice.project?.client?.name || "N/A",
        clientAddress: getClientAddress(),
        // companyName: tenantName || undefined,
        companyName: "RockForce",
        companyAddress: getRemittanceAddress(),
        terms: invoice.meta_data?.terms,
        purchaseOrderNumber: invoice.meta_data?.purchase_order_number,
      },
      lineItemGroups: groups?.map((g) => ({
        shiftName: g.shiftName,
        subtotal: g.subtotal,
        positions: g.positions.map((p) => ({
          positionName: p.positionName,
          subtotal: p.subtotal,
          items: p.items.map((item) => ({
            description: item.description,
            quantity: item.quantity,
            rate: item.unit_price,
            amount: item.amount,
            time_in: item.time_in,
            time_out: item.time_out,
            admin_fee: item.admin_fee,
            discount: item.discount,
          })),
        })),
      })),
      lineItems: lineItems.map((item) => ({
        description: item.description,
        quantity: item.quantity,
        rate: item.unit_price,
        amount: item.amount,
        time_in: item.time_in,
        time_out: item.time_out,
        admin_fee: item.admin_fee,
        discount: item.discount,
      })),
      adjustments: adjustments
        .filter((a) => a.amount !== 0)
        .map((a) => ({ description: a.description || "Adjustment", amount: a.amount })),
      depositApplied: invoice.meta_data?.deposit_applied || undefined,
      totals: {
        subtotal,
        total: invoice.meta_data?.amount_due || subtotal,
      },
      notes: invoice.meta_data?.terms || "",
      attachments: attachments || [],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Get the current session token
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) {
      throw new Error("No authentication token available");
    }

    // Call the edge function directly with fetch
    const response = await fetch(`${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/pdf-create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(pdfData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Edge function error: ${errorText}`);
    }

    // Response is a binary PDF blob
    const blob = await response.blob();
    return blob;
  };

  const handleRegeneratePDF = async (fileOptions: Map<string, any>) => {
    if (!invoice) return;

    try {
      setIsGeneratingPDF(true);

      // Fetch and convert each file to base64
      const attachmentsWithData: { data: string; name: string; fit: string; align: string; valign: string }[] = [];
      for (const [fileId, fileOption] of fileOptions.entries()) {
        try {
          // Fetch the file using the signed URL
          const response = await fetch(fileOption.url);
          if (!response.ok) {
            console.error(`Failed to fetch file: ${fileOption.name}`, response.status);
            continue;
          }

          // Convert to blob then to base64
          const blob = await response.blob();

          const base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              // Extract base64 data (remove data URL prefix)
              const base64 = result.split(",")[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          attachmentsWithData.push({
            data: base64Data,
            name: fileOption.name,
            fit: fileOption.fit,
            align: fileOption.align,
            valign: fileOption.valign,
          });
        } catch (error) {
          console.error(`Error processing attachment ${fileOption.name}:`, error);
          // Continue with other attachments
        }
      }

      const newBlob = await generatePDF(attachmentsWithData);
      if (newBlob) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const newDataUrl = reader.result as string;
          setCurrentPdfSrc(newDataUrl);
        };
        reader.readAsDataURL(newBlob);
      }
    } catch (error: any) {
      console.error("PDF regeneration error:", error);
      toast.showToast({
        title: "PDF Regeneration Failed",
        message: error.message || "Failed to regenerate PDF",
        toastType: "error",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleViewPDF = async () => {
    if (!invoice) {
      toast.showToast({
        title: "No invoice data",
        message: "Cannot generate PDF without invoice data",
        toastType: "error",
      });
      return;
    }

    setIsGeneratingPDF(true);

    try {
      const blob = await generatePDF();
      if (!blob) throw new Error("Failed to generate PDF");

      // Convert blob to data URL for PdfModal
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setCurrentPdfSrc(dataUrl);
        setPdfModalOpen(true);
      };
      reader.readAsDataURL(blob);
    } catch (error: any) {
      setPdfModalOpen(false);
      console.error("PDF generation error:", error);
      toast.showToast({
        title: "PDF Generation Failed",
        message: error.message || "Failed to generate invoice PDF",
        toastType: "error",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <View style={styles.tabContent}>
      <PreviewSection title="Invoice Details">
        <View style={{ flexDirection: "row", gap: 8 }}>
          <DateInput
            label="Invoice Date"
            mode="date"
            value={displayData?.meta_data?.invoice_date || new Date()}
            onChange={(date) =>
              editing &&
              updateFormData({
                ...formData,
                meta_data: { ...formData.meta_data, invoice_date: date },
              } as any)
            }
            editing={editing}
            disabled={disabled}
            loading={loading}
          />
          <DateInput
            label="Due Date"
            mode="date"
            value={
              displayData?.meta_data?.invoice_date && displayData?.meta_data?.terms
                ? (() => {
                    const days = parseInt((displayData.meta_data.terms || "").replace(/\D/g, ""), 10);
                    if (!days) return displayData.meta_data.invoice_date;
                    const due = new Date(displayData.meta_data.invoice_date);
                    due.setDate(due.getDate() + days);
                    return due;
                  })()
                : undefined
            }
            onChange={() => {}}
            editing={false}
            disabled={true}
            loading={loading}
          />
        </View>
        <ThemedInput
          label="Purchase Order"
          value={displayData?.meta_data?.purchase_order_number || ""}
          onChangeText={(text) =>
            editing &&
            updateFormData({
              ...formData,
              meta_data: { ...formData.meta_data, purchase_order_number: text },
            } as any)
          }
          editable={!disabled}
          placeholder={disabled ? "—" : "PO number"}
        />
        <ThemedInput
          label="Amount Due"
          value={displayData?.meta_data?.amount_due ? formatCurrency(displayData.meta_data.amount_due) : ""}
          onChangeText={(text) =>
            editing &&
            updateFormData({
              ...formData,
              meta_data: { ...formData.meta_data, amount_due: parseFloat(text.replace(/[^0-9.]/g, "")) || 0 },
            } as any)
          }
          keyboardType="numeric"
          editable={!disabled}
          placeholder={disabled ? "—" : "$0.00"}
        />
        <EnumInput
          label="Terms"
          options={[...INVOICE_TERMS]}
          value={displayData?.meta_data?.terms}
          placeholder={disabled ? "—" : "Select terms..."}
          disabled={disabled}
          onSelect={(value) =>
            editing &&
            updateFormData({
              ...formData,
              meta_data: { ...formData.meta_data, terms: value },
            } as any)
          }
        />
        <EnumInput
          label="Remittance Address"
          options={addresses.map((a) => a.name)}
          value={currentAddressName}
          placeholder={disabled ? "—" : "Select address..."}
          disabled={disabled}
          onSelect={(name) => {
            if (!editing) return;
            const address = addresses.find((a) => a.name === name);
            if (address) {
              updateFormData({
                ...formData,
                meta_data: { ...formData.meta_data, remittance_address_id: address.id },
              } as any);
            }
          }}
        />
      </PreviewSection>

      <PreviewSection title={`${displayNames?.projects?.name || "Project"} Details`}>
        <ClientInput
          value={displayData?.client_id || displayData?.project?.client_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.clients?.name || "Client"}
          onChange={(clientId) =>
            updateFormData({
              ...formData,
              client_id: clientId,
              project_id: undefined,
            } as any)
          }
        />
        <ProjectInput
          value={displayData?.project_id || null}
          clientId={displayData?.client_id || displayData?.project?.client_id}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          showDeposit
          label={displayNames?.projects?.name || "Project"}
          onChange={(projectId) =>
            updateFormData({
              ...formData,
              project_id: projectId,
            } as any)
          }
        />
        {typeof projectDeposit === "number" && projectDeposit > 0 && (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            {editing ? (
              <>
                <View style={{ flex: 1 }}>
                  <NumericInput value={depositApplied} onChangeValue={handleDepositChange} placeholder="0.00" />
                </View>
                <ThemedText style={{ fontSize: 12, color: colors.textSecondary }}>
                  of {formatCurrency(projectDeposit)} deposit
                </ThemedText>
              </>
            ) : (
              <ThemedText style={{ fontSize: 13, color: colors.textSecondary }}>
                Deposit Applied: {depositApplied > 0 ? formatCurrency(depositApplied) : "—"} of{" "}
                {formatCurrency(projectDeposit)}
              </ThemedText>
            )}
          </View>
        )}
      </PreviewSection>

      <PreviewSection title="Line Items">
        <View style={{ flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <Pressable style={{ flexDirection: "row", alignItems: "center", gap: 6 }} onPress={toggleCombineAdminFee}>
            <MaterialCommunityIcons
              name={combineAdminFee ? "checkbox-marked" : "checkbox-blank-outline"}
              size={20}
              color={combineAdminFee ? colors.primary : colors.textSecondary}
            />
            <ThemedText style={{ fontSize: 13, color: colors.textSecondary }}>Combine Admin Fee</ThemedText>
          </Pressable>
          <Pressable style={{ flexDirection: "row", alignItems: "center", gap: 6 }} onPress={toggleCombineDiscount}>
            <MaterialCommunityIcons
              name={combineDiscount ? "checkbox-marked" : "checkbox-blank-outline"}
              size={20}
              color={combineDiscount ? colors.primary : colors.textSecondary}
            />
            <ThemedText style={{ fontSize: 13, color: colors.textSecondary }}>Combine Discount</ThemedText>
          </Pressable>
          {editing && (
            <Pressable style={{ flexDirection: "row", alignItems: "center", gap: 4 }} onPress={addAdjustment}>
              <MaterialCommunityIcons name="plus" size={20} color={colors.primary} />
              <ThemedText style={{ fontSize: 13, color: colors.primary }}>Adjustment</ThemedText>
            </Pressable>
          )}
        </View>

        {/* Adjustment Inputs */}
        {adjustments.length > 0 && (
          <View style={{ gap: 8, marginBottom: 8 }}>
            {adjustments.map((adj) => (
              <View
                key={adj.id}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  backgroundColor: colors.backgroundSecondary,
                  borderRadius: 8,
                  borderWidth: StyleSheet.hairlineWidth,
                  borderColor: colors.border,
                  padding: 8,
                }}
              >
                <ThemedInput
                  placeholder="Description"
                  value={adj.description}
                  onChangeText={(v) => updateAdjustment(adj.id, { description: v })}
                  style={{ flex: 2 }}
                />
                <NumericInput
                  value={adj.amount}
                  onChangeValue={(v) => updateAdjustment(adj.id, { amount: v })}
                  style={{ flex: 1 }}
                />
                <Pressable
                  onPress={() => removeAdjustment(adj.id)}
                  style={{
                    padding: 6,
                    borderRadius: 6,
                    backgroundColor: colors.background,
                  }}
                >
                  <MaterialCommunityIcons name="undo" size={18} color={colors.textSecondary} />
                </Pressable>
              </View>
            ))}
          </View>
        )}

        {(() => {
          const rawLineItems = displayData?.meta_data?.line_items || [];

          if (rawLineItems.length === 0 && adjustments.filter((a) => a.amount !== 0).length === 0) {
            return <ThemedText style={{ opacity: 0.5 }}>No line items</ThemedText>;
          }

          const {
            items: combinedItems,
            adminFeeTotal,
            discountTotal,
          } = combineLineItems(rawLineItems, combineAdminFee, combineDiscount);
          let lineItems = combinedItems;

          // Append adjustments to display
          const adjItems = adjustments
            .filter((a) => a.amount !== 0)
            .map((a) => ({
              description: a.description || "Adjustment",
              unit_price: a.amount,
              quantity: 1,
              amount: a.amount,
            }));
          if (adjItems.length > 0) {
            lineItems = [...lineItems, ...adjItems];
          }

          // Totals footer: Subtotal is the pre-adjustment base (Σ line amounts
          // before the combined fee/discount), then + Admin Fee − Discount = Total.
          const grandTotal = calculateLineItemTotal();
          const subtotal = round2(grandTotal - adminFeeTotal + discountTotal);
          const totalsFooter = (
            <View style={styles.totalsFooter}>
              <View style={styles.totalsLine}>
                <ThemedText style={styles.totalsLabel}>Subtotal</ThemedText>
                <ThemedText style={styles.totalsValue}>{formatCurrency(subtotal)}</ThemedText>
              </View>
              {combineAdminFee && adminFeeTotal > 0 ? (
                <View style={styles.totalsLine}>
                  <ThemedText style={styles.totalsLabel}>Admin Fee</ThemedText>
                  <ThemedText style={styles.totalsValue}>{formatCurrency(adminFeeTotal)}</ThemedText>
                </View>
              ) : null}
              {combineDiscount && discountTotal > 0 ? (
                <View style={styles.totalsLine}>
                  <ThemedText style={styles.totalsLabel}>Discount</ThemedText>
                  <ThemedText style={styles.totalsValue}>{formatCurrency(-discountTotal)}</ThemedText>
                </View>
              ) : null}
              <View style={[styles.totalsLine, styles.totalsGrand]}>
                <ThemedText type="defaultSemiBold">Total</ThemedText>
                <ThemedText type="defaultSemiBold">{formatCurrency(grandTotal)}</ThemedText>
              </View>
            </View>
          );

          const groups = groupLineItems(lineItems);
          const hasGroups = groups.length > 1 || (groups.length === 1 && groups[0].shiftName !== "Other");

          if (!hasGroups) {
            // Flat rendering for ungrouped items
            return (
              <View>
                {lineItems.map((item, index) => (
                  <View key={index} style={styles.lineItem}>
                    <View style={styles.lineItemHeader}>
                      <ThemedText type="defaultSemiBold">{item.description}</ThemedText>
                      <ThemedText type="defaultSemiBold">{formatCurrency(item.amount)}</ThemedText>
                    </View>
                    <ThemedText style={styles.lineItemDetail}>
                      {item.quantity} × {formatCurrency(item.unit_price)}
                      {!combineAdminFee && item.admin_fee ? `  |  Admin Fee: ${item.admin_fee}%` : ""}
                      {!combineDiscount && item.discount ? `  |  Discount: ${item.discount}%` : ""}
                    </ThemedText>
                  </View>
                ))}
                {totalsFooter}
              </View>
            );
          }

          // Grouped rendering
          return (
            <View>
              {groups.map((shiftGroup, gi) => (
                <View key={`shift-${gi}`} style={{ marginBottom: gi < groups.length - 1 ? 12 : 0 }}>
                  {/* Shift Header */}
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingVertical: 6,
                      paddingLeft: 8,
                      borderLeftWidth: 3,
                      borderLeftColor: colors.primary,
                      marginBottom: 4,
                    }}
                  >
                    <ThemedText style={{ fontWeight: "700", fontSize: 14 }}>{shiftGroup.shiftName}</ThemedText>
                    <ThemedText style={{ fontWeight: "600", fontSize: 13 }}>
                      {formatCurrency(shiftGroup.subtotal)}
                    </ThemedText>
                  </View>

                  {shiftGroup.positions.map((posGroup, pi) => (
                    <View key={`pos-${gi}-${pi}`}>
                      {/* Position Sub-header */}
                      {(shiftGroup.positions.length > 1 || posGroup.positionName !== "General") && (
                        <View style={{ paddingLeft: 16, paddingVertical: 2, marginBottom: 2 }}>
                          <ThemedText style={{ fontSize: 12, fontStyle: "italic", opacity: 0.6 }}>
                            {posGroup.positionName}
                          </ThemedText>
                        </View>
                      )}

                      {/* Line Items */}
                      {posGroup.items.map((item, ii) => (
                        <View key={`item-${gi}-${pi}-${ii}`} style={[styles.lineItem, { paddingLeft: 16 }]}>
                          <View style={styles.lineItemHeader}>
                            <ThemedText type="defaultSemiBold">{item.description}</ThemedText>
                            <ThemedText type="defaultSemiBold">{formatCurrency(item.amount)}</ThemedText>
                          </View>
                          <ThemedText style={styles.lineItemDetail}>
                            {item.quantity} × {formatCurrency(item.unit_price)}
                            {!combineAdminFee && item.admin_fee ? `  |  Admin Fee: ${item.admin_fee}%` : ""}
                            {!combineDiscount && item.discount ? `  |  Discount: ${item.discount}%` : ""}
                          </ThemedText>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              ))}
              {totalsFooter}
            </View>
          );
        })()}

        {editing && (
          <ThemedText style={{ opacity: 0.5, fontStyle: "italic", marginTop: 8 }}>
            Line item editing will be implemented in a future update
          </ThemedText>
        )}
      </PreviewSection>

      {/* PDF View Button */}
      {!editing && (
        <View style={styles.pdfSection}>
          <Button
            text={isGeneratingPDF ? "Generating PDF..." : "View Invoice PDF"}
            iconPosition="right"
            icon="file-pdf-box"
            onPress={handleViewPDF}
            enabled={!isGeneratingPDF}
            style={[styles.pdfButton, { backgroundColor: colors.red?.background || "#fee2e2" }]}
            textStyle={{ color: colors.red?.text || "#dc2626" }}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  tabContent: {
    flex: 1,
    gap: 16,
    padding: 16,
    overflowY: "scroll",
  },
  lineItem: {
    paddingVertical: 8,
  },
  lineItemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  lineItemDetail: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 2,
  },
  totalsFooter: {
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#e5e7eb",
    alignSelf: "flex-end",
    minWidth: 220,
    gap: 2,
  },
  totalsLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalsLabel: {
    fontSize: 13,
    opacity: 0.7,
  },
  totalsValue: {
    fontSize: 13,
  },
  totalsGrand: {
    marginTop: 4,
    paddingTop: 6,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#e5e7eb",
  },
  pdfSection: {
    marginTop: 8,
    paddingTop: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#e5e7eb",
  },
  pdfButton: {
    paddingVertical: 12,
    borderRadius: 8,
  },
});
